import { useEffect, useState } from 'react';
import {
  useTranslate,
  useNotification,
  useInvalidate,
  useCustomMutation,
  useNavigation,
} from '@refinedev/core';
import {
  Modal,
  Form,
  InputNumber,
  Input,
  Radio,
  DatePicker,
  Alert,
  Select,
  type SelectProps,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { extractBackendMessage } from '@/utils/apiError';
import type {
  EntitlementDetailOut,
  EntitlementIssueIn,
} from '@/api/generated/schemas';
import { Tier } from '@/api/generated/schemas';

const TIERS = Object.values(Tier);
type Mode = 'grant' | 'prolong';

type GrantValues = {
  account_id: number;
  tier: Tier;
  validity: [Dayjs, Dayjs];
  reason: string;
};

interface GrantEntitlementModalProps {
  open: boolean;
  onClose: () => void;
  /** 'grant' (default) or 'prolong'; drives titles, button labels, disclaimer. */
  mode?: Mode;
  /** When set, account field is pre-filled (and locked unless `accountOptions`). */
  initialAccountId?: number;
  /** When set, account is picked from these instead of typed in. */
  accountOptions?: SelectProps['options'];
  /** When set, overrides the default now → +7d validity range. */
  initialValidity?: [Dayjs, Dayjs];
}

const defaultValidity = (): [Dayjs, Dayjs] => {
  const from = dayjs();
  return [from, from.add(7, 'day')];
};

export const GrantEntitlementModal = ({
  open,
  onClose,
  mode = 'grant',
  initialAccountId,
  accountOptions,
  initialValidity,
}: GrantEntitlementModalProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const invalidate = useInvalidate();
  const { show } = useNavigation();
  const { mutateAsync } = useCustomMutation();
  const [form] = Form.useForm<GrantValues>();
  const [submitting, setSubmitting] = useState(false);

  const tk = (suffix: string) =>
    t(`monetization.entitlements.${mode}.${suffix}`);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        account_id: initialAccountId,
        validity: initialValidity ?? defaultValidity(),
      });
    } else {
      form.resetFields();
      setSubmitting(false);
    }
  }, [open, form, initialAccountId, initialValidity]);

  const handleSubmit = async () => {
    let values: GrantValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const [from, to] = values.validity;
    const payload: EntitlementIssueIn = {
      account_id: values.account_id,
      tier: values.tier,
      valid_from: from.toISOString(),
      valid_until: to.toISOString(),
      reason: values.reason.trim(),
    };

    setSubmitting(true);
    try {
      const result = await mutateAsync({
        url: 'monetization/entitlements/issue',
        method: 'post',
        values: payload,
        successNotification: false,
        errorNotification: false,
      });
      const granted = result?.data as EntitlementDetailOut | undefined;

      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: tk('success'),
      });
      invalidate({
        resource: 'monetization.entitlements',
        invalidates: ['list'],
      });
      onClose();
      if (granted?.id != null) {
        show('monetization.entitlements', granted.id);
      }
    } catch (error) {
      const description = extractBackendMessage(error) ?? tk('error_fallback');
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={tk('title')}
      okText={tk('submit')}
      okButtonProps={{ loading: submitting }}
      cancelText={t('common.cancel')}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      maskClosable={!submitting}
      onCancel={onClose}
      onOk={handleSubmit}
      destroyOnClose
      width={520}
    >
      {mode === 'prolong' && (
        <Alert
          type="info"
          showIcon
          message={tk('disclaimer')}
          style={{ marginBottom: 16 }}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={submitting}
        initialValues={{
          account_id: initialAccountId,
          validity: initialValidity ?? defaultValidity(),
        }}
      >
        <Form.Item
          name="account_id"
          label={t('monetization.entitlements.grant.fields.account_id')}
          rules={[
            {
              required: true,
              message: t(
                'monetization.entitlements.grant.errors.account_required'
              ),
            },
            {
              type: 'integer',
              min: 1,
              message: t(
                'monetization.entitlements.grant.errors.account_invalid'
              ),
            },
          ]}
        >
          {accountOptions ? (
            <Select options={accountOptions} />
          ) : (
            <InputNumber
              style={{ width: '100%' }}
              placeholder="12345"
              min={1}
              precision={0}
              disabled={initialAccountId != null}
            />
          )}
        </Form.Item>

        <Form.Item
          name="tier"
          label={t('monetization.entitlements.grant.fields.tier')}
          rules={[
            {
              required: true,
              message: t(
                'monetization.entitlements.grant.errors.tier_required'
              ),
            },
          ]}
        >
          <Radio.Group
            optionType="button"
            options={TIERS.map((value) => ({ label: value, value }))}
          />
        </Form.Item>

        <Form.Item
          name="validity"
          label={t('monetization.entitlements.grant.fields.validity')}
          rules={[
            {
              required: true,
              message: t(
                'monetization.entitlements.grant.errors.validity_required'
              ),
            },
            {
              validator: (_, value: [Dayjs, Dayjs] | undefined) => {
                if (!value || !value[0] || !value[1]) return Promise.resolve();
                if (!value[1].isAfter(value[0])) {
                  return Promise.reject(
                    new Error(
                      t('monetization.entitlements.grant.errors.validity_order')
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          extra={
            mode === 'grant'
              ? t('monetization.entitlements.grant.hints.validity')
              : undefined
          }
        >
          <DatePicker.RangePicker showTime style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="reason"
          label={t('monetization.entitlements.grant.fields.reason')}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t(
                'monetization.entitlements.grant.errors.reason_required'
              ),
            },
            { max: 500 },
          ]}
        >
          <Input.TextArea
            rows={3}
            maxLength={500}
            showCount
            placeholder={t('monetization.entitlements.grant.hints.reason')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
